/**
 * BoxLocationMap 컴포넌트
 *
 * 박스 위치 영역, 구역, 마커를 표시하는 Google Maps 컴포넌트
 * 부모 컴포넌트로부터 props로 데이터를 받아 지도를 렌더링합니다.
 */

import { useEffect, useRef, useCallback, forwardRef, useImperativeHandle, useState } from 'react';
import { PolygonPresets } from './MapPolygon';
import {
  createMarker,
  updateMarkerSelection,
  updateMarkerSize,
  DEFAULT_MARKER_SIZE,
} from './MapMarker';
import { Zone, Coordinate } from 'models/setup/mobile/BoxLocation';
import {
  retryWithNextKey,
  isKeysExhausted,
  setKeysExhausted,
  initializeGlobalMap,
  attachMapTo,
  detachMap,
  destroyGlobalMap,
} from 'utils/LoadGoogleMaps';

// ============================================================================
// 유틸리티 함수
// ============================================================================

/**
 * debounce 함수 - 연속 호출 시 마지막 호출만 실행
 */
const debounce = <T extends (...args: any[]) => void>(fn: T, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

/**
 * zoom 레벨에 따른 마커 크기 계산
 * @param zoom - 현재 지도 zoom 레벨
 * @param selected - 선택 상태
 * @returns 계산된 마커 크기
 */
const getMarkerSizeByZoom = (zoom: number, selected: boolean): number => {
  const baseSize = selected ? DEFAULT_MARKER_SIZE.selected : DEFAULT_MARKER_SIZE.normal;
  // zoom 18 기준 1배, 범위 0.25배 ~ 4배로 확대
  // zoom 10 → 0.25x, zoom 14 → 0.5x, zoom 18 → 1x, zoom 22 → 2x, zoom 26+ → 4x
  const scale = Math.max(0.25, Math.min(4, Math.pow(2, (zoom - 18) / 4)));
  return Math.round(baseSize * scale);
};

// ============================================================================
// 타입 정의
// ============================================================================

/**
 * 마커 데이터 타입
 */
export interface MarkerData {
  id: string;
  name: string;
  position: { lat: number; lng: number };
  selected: boolean;
  color: string;
}

/**
 * BoxLocationMap Props
 */
export interface BoxLocationMapProps {
  areaCoordinates?: Coordinate[]; // 영역 좌표 (bounds 계산용) - optional
  zones?: Zone[]; // 구역 목록 - optional
  markers?: MarkerData[]; // 마커 목록 - optional
  onMarkerClick?: (markerId: string) => void; // 마커 클릭 콜백 - optional
  fitBoundsTarget?: 'area' | 'zone' | 'none'; // fitBounds 대상: area(대표구역), zone(선택구역), none(유지)
  zoneBounds?: Coordinate[]; // 구역 선택 시 해당 구역 좌표
  onFitBoundsComplete?: () => void; // fitBounds 완료 후 콜백
}

/**
 * BoxLocationMap Ref Handle (부모에서 호출 가능한 메서드)
 */
export interface BoxLocationMapRef {
  focusZone: (coordinates: Coordinate[]) => void;
}

// ============================================================================
// 메인 컴포넌트
// ============================================================================

const BoxLocationMap = forwardRef<BoxLocationMapRef, BoxLocationMapProps>(
  (
    {
      areaCoordinates,
      zones = [],
      markers = [],
      onMarkerClick,
      fitBoundsTarget = 'none',
      zoneBounds,
      onFitBoundsComplete,
    },
    ref
  ) => {
    // --- State 선언 ---
    const [allKeysExhausted, setAllKeysExhausted] = useState(false);
    const mapRef = useRef<google.maps.Map | null>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);

    // 생성된 폴리곤 인스턴스를 저장 (정리용)
    const polygonsRef = useRef<google.maps.Polygon[]>([]);
    // 마커를 Map으로 관리 (id → marker 인스턴스)
    const markersMapRef = useRef<Map<string, google.maps.Marker>>(new Map());
    // 이전 markers props를 저장 (비교용)
    const prevMarkersRef = useRef<MarkerData[]>([]);
    // 현재 zoom 레벨 저장
    const currentZoomRef = useRef<number>(15);
    // zoom 이벤트 리스너 저장 (cleanup용)
    const zoomListenerRef = useRef<google.maps.MapsEventListener | null>(null);

    // --- 부모에서 호출 가능한 메서드 노출 ---
    useImperativeHandle(ref, () => ({
      focusZone: (coordinates: Coordinate[]) => {
        if (!mapRef.current || !coordinates || coordinates.length === 0) return;

        const map = mapRef.current;
        const bounds = new google.maps.LatLngBounds();

        coordinates.forEach((coord) => {
          if (coord.xcrd && coord.ycrd) {
            bounds.extend({
              lat: parseFloat(coord.xcrd),
              lng: parseFloat(coord.ycrd),
            });
          }
        });

        // 해당 구역에 맵 확대/이동
        map.fitBounds(bounds);
      },
    }));

    // --- 마커 크기 업데이트 함수 (zoom 변경 시 호출) ---
    const updateAllMarkerSizes = useCallback((zoom: number) => {
      const currentMarkersMap = markersMapRef.current;
      const prevMarkers = prevMarkersRef.current;

      currentMarkersMap.forEach((marker, id) => {
        // 해당 마커의 selected 상태 찾기
        const markerData = prevMarkers.find((m, i) => (m.id || `marker-${i}`) === id);
        const selected = markerData?.selected || false;
        const color = markerData?.color || '';
        const size = getMarkerSizeByZoom(zoom, selected);
        updateMarkerSize(marker, selected, color, size);
      });
    }, []);

    // ============================================================================
    // Google Maps 초기화 및 에러 감지
    // ============================================================================
    /**
     * Google Maps 초기화 및 에러 감지
     *
     * 핵심 로직:
     * 1. 키 소진 여부 먼저 체크 → 소진됐으면 로드 시도 안 함 (API 호출 절약)
     * 2. MutationObserver로 에러 DOM 실시간 감지
     * 3. 에러 감지 시 다음 키로 자동 전환
     * 4. 모든 키 소진 시 안내 메시지 표시
     * 5. 맵 인스턴스 재사용으로 API 호출 최소화 (initializeGlobalMap 사용)
     */
    useEffect(() => {
      if (!mapContainerRef.current) return;

      // 키 소진 여부 먼저 확인 (17시 기준 자동 초기화)
      // → 소진됐으면 API 호출 없이 바로 안내 메시지 표시
      if (isKeysExhausted()) {
        setAllKeysExhausted(true);
        return;
      }

      let observer: MutationObserver | null = null;
      /** 중복 재시도 방지 플래그 (MutationObserver가 여러 번 트리거될 수 있음) */
      let isRetrying = false;

      /**
       * 에러 감지 시 다음 키로 재시도
       *
       * - isRetrying 플래그로 중복 호출 방지
       * - destroyGlobalMap()으로 에러난 맵 파괴 후 새 키로 재시도
       * - retryWithNextKey() 성공 시 → initMap() 재호출
       * - 실패 시 (모든 키 소진) → 플래그 저장 + 안내 메시지 표시
       */
      const handleRetry = async () => {
        if (isRetrying) return; // 중복 호출 방지
        isRetrying = true;

        // 에러난 맵 인스턴스 파괴 (새 키로 재생성 필요)
        destroyGlobalMap();

        const success = await retryWithNextKey();

        if (success) {
          // 다음 키로 재시도 성공 → 새 맵 초기화
          isRetrying = false;
          initMap();
        } else {
          // 모든 키 소진 → 플래그 저장하고 안내 메시지 표시
          setKeysExhausted();
          if (mapContainerRef.current) {
            mapContainerRef.current.innerHTML = ''; // 에러 UI 제거
          }
          mapRef.current = null;
          setAllKeysExhausted(true);
        }
      };

      /**
       * Google Maps 초기화 함수
       *
       * initializeGlobalMap을 사용하여 맵 인스턴스를 재사용
       * - 첫 호출: 새 맵 생성 (API 호출 발생)
       * - 이후 호출: 기존 맵 반환 (API 호출 없음)
       *
       * MutationObserver를 먼저 설정한 후 맵을 생성하여
       * 에러 DOM이 추가되는 즉시 감지할 수 있도록 함
       */
      const initMap = async () => {
        /**
         * MutationObserver로 Google Maps 에러 DOM 감지
         *
         * 왜 MutationObserver인가?
         * - OverQuotaMapError 발생 시 Google Maps가 에러 UI를 DOM에 추가함
         * - gm_authFailure 콜백은 InvalidKeyMapError만 감지 (OverQuotaMapError는 감지 못함)
         * - setTimeout/polling 방식은 타이밍 이슈 발생 (특히 콜드 캐시 상황)
         * - MutationObserver는 DOM 변경 즉시 감지하므로 타이밍 문제 없음
         *
         * 감지 대상:
         * - .dismissButton: 에러 모달의 닫기 버튼
         * - .gm-err-container: 에러 메시지 컨테이너
         */
        observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            Array.from(mutation.addedNodes).forEach((node) => {
              if (node instanceof HTMLElement) {
                // Google Maps 에러 UI 요소 감지
                if (
                  node.classList.contains('dismissButton') ||
                  node.classList.contains('gm-err-container') ||
                  node.querySelector?.('.dismissButton') ||
                  node.querySelector?.('.gm-err-container')
                ) {
                  observer?.disconnect(); // 감지 후 observer 해제 (중복 처리 방지)
                  handleRetry(); // 다음 키로 재시도
                }
              }
            });
          });
        });

        // mapContainer의 모든 자식 변경 감지 (subtree: true로 깊은 DOM까지)
        observer.observe(mapContainerRef.current!, {
          childList: true,
          subtree: true,
        });

        try {
          // 전역 맵 초기화 또는 재사용 (API 호출 최소화)
          const map = await initializeGlobalMap({
            zoom: 15,
            center: { lat: 33.2815, lng: -111.5526 },
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
          });

          // 맵 DOM을 현재 컨테이너로 이동 (화면에 표시)
          attachMapTo(mapContainerRef.current!);

          mapRef.current = map;
          currentZoomRef.current = map.getZoom() || 15;
        } catch (error) {
          console.error('Google Maps 초기화 실패:', error);
          if (error instanceof Error && error.message === '모든 API 키 소진') {
            setKeysExhausted();
            setAllKeysExhausted(true);
          }
        }
      };

      initMap();

      // cleanup: 컴포넌트 언마운트 시 맵 DOM을 holder로 복귀 (파괴 아님!)
      return () => {
        observer?.disconnect();
        detachMap(); // 맵 DOM을 holder로 복귀하여 재사용 가능하게 유지
      };
    }, [updateAllMarkerSizes]);

    // --- Convex Hull 알고리즘 ---
    const computeConvexHull = (points: Array<{ lat: number; lng: number }>) => {
      if (points.length < 3) return points;

      // 1. 가장 아래/왼쪽 점 찾기 (pivot)
      let lowest = 0;
      for (let i = 1; i < points.length; i++) {
        if (
          points[i].lat < points[lowest].lat ||
          (points[i].lat === points[lowest].lat && points[i].lng < points[lowest].lng)
        ) {
          lowest = i;
        }
      }

      const p0 = points[lowest];
      const others = points.filter((_, i) => i !== lowest);

      // 2. pivot 기준 극각(polar angle) 정렬
      const sorted = others.sort((a, b) => {
        const angleA = Math.atan2(a.lat - p0.lat, a.lng - p0.lng);
        const angleB = Math.atan2(b.lat - p0.lat, b.lng - p0.lng);
        if (angleA !== angleB) return angleA - angleB;

        // 각도가 같으면 거리로 정렬
        const distA = Math.hypot(a.lat - p0.lat, a.lng - p0.lng);
        const distB = Math.hypot(b.lat - p0.lat, b.lng - p0.lng);
        return distA - distB;
      });

      // 3. Graham Scan으로 Convex Hull 구축
      const hull = [p0, sorted[0]];
      for (let i = 1; i < sorted.length; i++) {
        while (
          hull.length > 1 &&
          cross(hull[hull.length - 2], hull[hull.length - 1], sorted[i]) <= 0
        ) {
          hull.pop();
        }
        hull.push(sorted[i]);
      }

      return hull;
    };

    // Cross product 계산 (방향 판단)
    const cross = (
      O: { lat: number; lng: number },
      A: { lat: number; lng: number },
      B: { lat: number; lng: number }
    ): number => {
      return (A.lng - O.lng) * (B.lat - O.lat) - (A.lat - O.lat) * (B.lng - O.lng);
    };

    // --- 마커 ID 생성 헬퍼 (id가 없는 경우 대비) ---
    const getMarkerId = useCallback((markerData: MarkerData, index: number): string => {
      return markerData.id || `marker-${index}`;
    }, []);

    // --- fitBounds 처리 (fitBoundsTarget 변경 시만 실행) ---
    useEffect(() => {
      if (!mapRef.current || fitBoundsTarget === 'none') return;

      const map = mapRef.current;
      const bounds = new google.maps.LatLngBounds();
      let didFitBounds = false;

      if (fitBoundsTarget === 'area' && areaCoordinates && areaCoordinates.length >= 3) {
        // 대표구역으로 fitBounds
        areaCoordinates.forEach((coord) => {
          if (coord.xcrd && coord.ycrd) {
            bounds.extend({
              lat: parseFloat(coord.xcrd),
              lng: parseFloat(coord.ycrd),
            });
          }
        });
        map.fitBounds(bounds);
        didFitBounds = true;
      } else if (fitBoundsTarget === 'zone' && zoneBounds && zoneBounds.length >= 3) {
        // 선택된 구역으로 fitBounds
        zoneBounds.forEach((coord) => {
          if (coord.xcrd && coord.ycrd) {
            bounds.extend({
              lat: parseFloat(coord.xcrd),
              lng: parseFloat(coord.ycrd),
            });
          }
        });
        map.fitBounds(bounds);
        didFitBounds = true;
      }

      // fitBounds 완료 후 콜백 호출 (부모에서 target을 'none'으로 리셋)
      if (didFitBounds && onFitBoundsComplete) {
        onFitBoundsComplete();
      }
    }, [fitBoundsTarget, areaCoordinates, zoneBounds, onFitBoundsComplete]);

    // --- 영역/폴리곤 렌더링 (areaCoordinates, zones 변경 시) ---
    useEffect(() => {
      if (!mapRef.current) return;

      const map = mapRef.current;

      // 1. 기존 폴리곤만 제거
      polygonsRef.current.forEach((polygon) => polygon.setMap(null));
      polygonsRef.current = [];

      // 2. 구역 폴리곤 생성 (Convex Hull 적용)
      zones.forEach((zone) => {
        if (!zone.coordinates || zone.coordinates.length < 3) return;

        // Coordinate[] → { lat, lng }[] 변환
        const convertedCoords = zone.coordinates
          .filter((coord) => coord.xcrd && coord.ycrd)
          .map((coord) => ({
            lat: parseFloat(coord.xcrd!),
            lng: parseFloat(coord.ycrd!),
          }));

        if (convertedCoords.length < 3) return;

        // Convex Hull 계산
        const hullCoords = computeConvexHull(convertedCoords);

        // 색상은 cyan으로 고정
        const zonePolygon = new google.maps.Polygon({
          paths: hullCoords,
          map: map,
          ...PolygonPresets['cyan'],
        });

        polygonsRef.current.push(zonePolygon);
      });
    }, [areaCoordinates, zones]);

    // --- 마커 렌더링 (markers 변경 시 - 최적화) ---
    useEffect(() => {
      if (!mapRef.current) return;

      const map = mapRef.current;
      const currentMarkersMap = markersMapRef.current;
      const prevMarkers = prevMarkersRef.current;

      // 현재 마커 ID Set 생성
      const currentIds = new Set(markers.map((m, i) => getMarkerId(m, i)));
      // 이전 마커 ID Set 생성
      const prevIds = new Set(prevMarkers.map((m, i) => getMarkerId(m, i)));

      // 1. 삭제된 마커 제거 (이전에 있었는데 현재 없는 것)
      prevIds.forEach((id) => {
        if (!currentIds.has(id)) {
          const marker = currentMarkersMap.get(id);
          if (marker) {
            marker.setMap(null);
            currentMarkersMap.delete(id);
          }
        }
      });

      // 현재 zoom 레벨
      const currentZoom = currentZoomRef.current;

      // 2. 마커 생성 또는 업데이트
      markers.forEach((markerData, index) => {
        const id = getMarkerId(markerData, index);
        const existingMarker = currentMarkersMap.get(id);

        if (existingMarker) {
          // 기존 마커 존재 → selected 상태만 비교해서 아이콘 변경
          const prevMarkerData = prevMarkers.find((m, i) => getMarkerId(m, i) === id);
          if (prevMarkerData && prevMarkerData.selected !== markerData.selected) {
            const size = getMarkerSizeByZoom(currentZoom, markerData.selected);
            updateMarkerSelection(existingMarker, markerData.selected, markerData.color, size);
          }
        } else {
          // 새 마커 생성 (현재 zoom 레벨에 맞는 크기로)
          const size = getMarkerSizeByZoom(currentZoom, markerData.selected);
          const marker = createMarker(
            map,
            markerData.position,
            markerData.selected,
            markerData.color,
            {
              title: markerData.name,
            }
          );
          // 생성 후 zoom에 맞는 크기로 업데이트
          updateMarkerSize(marker, markerData.selected, markerData.color, size);

          // 마커 클릭 이벤트 리스너 추가
          if (onMarkerClick) {
            marker.addListener('click', () => {
              onMarkerClick(markerData.id);
            });
          }

          currentMarkersMap.set(id, marker);
        }
      });

      // 3. 현재 markers를 이전 markers로 저장
      prevMarkersRef.current = [...markers];
    }, [markers, onMarkerClick, getMarkerId]);

    // --- 컴포넌트 언마운트 시 정리 ---
    useEffect(() => {
      return () => {
        // cleanup: zoom 이벤트 리스너 제거
        if (zoomListenerRef.current) {
          google.maps.event.removeListener(zoomListenerRef.current);
        }
        // cleanup: 모든 폴리곤 제거
        polygonsRef.current.forEach((polygon) => polygon.setMap(null));
        // cleanup: 모든 마커 제거
        markersMapRef.current.forEach((marker) => marker.setMap(null));
        markersMapRef.current.clear();
      };
    }, []);

    // --- JSX 렌더링 ---
    if (allKeysExhausted) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            backgroundColor: '#f5f5f5',
            color: '#666',
            textAlign: 'center',
            padding: '20px',
            fontSize: '18px',
            fontWeight: 'bold',
            lineHeight: '1.6',
          }}
        >
          지도 사용량이 초과되었습니다.
          <br />
          사용량은 매일 오후 17시에 초기화됩니다.
        </div>
      );
    }

    return <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />;
  }
);

BoxLocationMap.displayName = 'BoxLocationMap';

export default BoxLocationMap;
